import SplashScreen from "@/components/splash-screen";
import StatusScreen from "@/components/status-screen";
import { Button } from "@/components/ui/button";
import { useOnboardMember } from "@/hooks/use-organisation";
import { useServices } from "@/services/hooks/useServices";
import { ClockUser } from "@phosphor-icons/react";
import { useSession, useActiveOrganization, useActiveTenancy } from "@wacht/tanstack-router";
import { AxiosError } from "axios";
import { CircleAlert, LogOut, RefreshCcw } from "lucide-react";
import React, { useEffect, useState, useRef } from "react";

const OnboardingWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { signOut } = useSession();
  const { activeOrganization } = useActiveOrganization();
  const { workspaceMembership } = useActiveTenancy();
  const { organisationService, userService } = useServices();
  const [isOnboardingAPIsDone, setIsOnboardingAPIsDone] = useState(false);
  const [onboardingError, setOnboardingError] = useState<Error | null>(null);
  const [showSetupLoader, setShowSetupLoader] = useState(false);
  const setupLoaderShownRef = useRef(false);

  // Call onboarding APIs before checking member status
  useEffect(() => {
    const callOnboardingAPIs = async () => {
      if (isOnboardingAPIsDone) return;
      try {
        

        // Call user onboarding API
        await userService.onboardUser();

        // Call organisation onboarding API
        await organisationService.onboardOrganisation();

        setIsOnboardingAPIsDone(true);
      } catch (error) {
        console.error("Error during onboarding:", error);
        setOnboardingError(error as Error);
        setIsOnboardingAPIsDone(true); // Set to true to allow error handling
      }
    };

    callOnboardingAPIs();
  }, [organisationService]);

  // check if user, org, and member is onboarded or not if not it will onboard them
  // Only call this after onboarding APIs are done



  const { isLoading, isError, error, data } = useOnboardMember(
    isOnboardingAPIsDone
  );

  const errorMessage = (error as AxiosError<{ message: string }>)?.response
    ?.data?.message as string;

  // Check if this is first-time onboarding after workspace creation
  useEffect(() => {
    // Only proceed if onboarding is complete and we have data
    if (!isOnboardingAPIsDone || isLoading || !data || setupLoaderShownRef.current) return;

    // Check if this is a new membership (first-time onboarding)
    const isNewMembership = data?.newMembership === true;
    
    if (!isNewMembership) return;

    // Get organization ID from data (most reliable source after onboarding)
    const orgId = data?.organisation?._id || activeOrganization?.id;
    const workspaceId = workspaceMembership?.workspace?.id;
    
    if (!orgId) return;

    // Create a unique key for this org/workspace combination
    const storageKey = workspaceId 
      ? `setup-loader-shown-${orgId}-${workspaceId}`
      : `setup-loader-shown-${orgId}`;

    // Check if we've already shown the setup loader for this org/workspace
    const hasShownLoader = localStorage.getItem(storageKey) === "true";

    if (!hasShownLoader) {
      // Mark that we're showing the loader to prevent re-running
      setupLoaderShownRef.current = true;
      
      // Show the setup loader
      setShowSetupLoader(true);
      
      // After 15 seconds, hide the loader and mark it as shown
      const timer = setTimeout(() => {
        setShowSetupLoader(false);
        localStorage.setItem(storageKey, "true");
      }, 15000);

      return () => clearTimeout(timer);
    }
  }, [isOnboardingAPIsDone, isLoading, data, activeOrganization?.id, workspaceMembership?.workspace?.id]);

  // Show loader while onboarding APIs are in progress or member check is in progress
  if (!isOnboardingAPIsDone || isLoading) {
    return <SplashScreen />;
  }

  // Show setup loader for 15 seconds on first-time onboarding
  if (showSetupLoader) {
    return <SplashScreen message="We are setting things up!" />;
  }

  // Show error if onboarding APIs failed
  if (onboardingError) {
    const onboardingErrorMessage =
      (onboardingError as AxiosError<{ message: string }>)?.response?.data
        ?.message || "Failed to complete onboarding. Please try again.";
    return (
      <ErrorScreen
        signOut={() => signOut()}
        errorMessage={onboardingErrorMessage}
      />
    );
  }

  if (isError) {
    return (
      <ErrorScreen signOut={() => signOut()} errorMessage={errorMessage} />
    );
  }

  if (!data?.organisation?.isVerified) {
    return <UnverifiedOrganizationScreen signOut={() => signOut()} />;
  }

  if (data?.isSuspended) {
    return <SuspendedMemberScreen signOut={() => signOut()} />;
  }

  return children;
};

export default OnboardingWrapper;

// Unverified organization screen
const UnverifiedOrganizationScreen: React.FC<{ signOut: () => void }> = ({
  signOut,
}) => {
  return (
    <StatusScreen
      title="Organization Verification Pending"
      description="Your organization is currently pending verification. This process may take some time. You'll be able to access the platform once the verification is complete. Our team will get in touch with you in 3 business days."
      variant="actions"
      actions={
        <div className="flex flex-col-reverse sm:flex-row gap-2">
          <Button
            onClick={() => {
              window.location.reload();
            }}
            className="min-w-[120px]"
            variant="outline"
          >
            <RefreshCcw />
            Refresh
          </Button>
          <Button onClick={signOut} className="min-w-[120px]">
            <LogOut />
            Sign Out
          </Button>
        </div>
      }
      icon={<ClockUser size={40} />}
      iconWrapperClassName="text-yellow-500 bg-yellow-50 dark:bg-yellow-900/20 dark:text-yellow-400"
    />
  );
};

// Suspended member screen
const SuspendedMemberScreen: React.FC<{ signOut: () => void }> = ({
  signOut,
}) => {
  return (
    <StatusScreen
      title="Account Suspended"
      description={
        "Your account is currently suspended. Please contact support."
      }
      variant="actions"
      actions={
        <div className="flex flex-col-reverse sm:flex-row gap-2">
          <Button
            onClick={() => {
              window.location.reload();
            }}
            variant="outline"
            className="min-w-[120px]"
          >
            <RefreshCcw />
            Refresh
          </Button>{" "}
          <Button onClick={signOut} className="min-w-[120px]">
            <LogOut />
            Sign Out
          </Button>
        </div>
      }
      iconWrapperClassName="text-amber-500 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-400"
      icon={<ClockUser size={40} />}
    />
  );
};

// Error screen
const ErrorScreen: React.FC<{ signOut: () => void; errorMessage: string }> = ({
  signOut,
  errorMessage,
}) => {
  return (
    <StatusScreen
      title="Error"
      description={
        errorMessage || "Something went wrong, please try again later."
      }
      variant="actions"
      actions={
        <div className="flex flex-col-reverse sm:flex-row gap-2">
          <Button
            onClick={() => {
              window.location.reload();
            }}
            variant="outline"
            className="min-w-[120px]"
          >
            <RefreshCcw />
            Refresh
          </Button>{" "}
          <Button onClick={signOut} className="min-w-[120px]">
            <LogOut />
            Sign Out
          </Button>
        </div>
      }
      iconWrapperClassName="text-red-500 bg-red-50 dark:bg-red-900/20 dark:text-red-400"
      icon={<CircleAlert size={40} />}
    />
  );
};
